import { useEffect, useRef, useState } from 'react';
import { Code, ExternalLink, X, ChevronLeft, ChevronRight, ArrowUpRight } from 'lucide-react';

const projects = [
  {
    name: 'Plateforme ADD - Boîte à Idées',
    desc: 'Une plateforme sécurisée pour exprimer vos idées afin de contribuer au développement des services de l’ADD.',
    stack: ['React.js', 'Node.js', 'IA / LLMs', 'Vite'],
    link: '#',
    images: ['/image11.png', '/Image111.png', '/Images1111.png'],
    num: '01',
  },
  {
    name: 'Plateforme de Scolarité & Administration',
    desc: 'Une solution de gestion académique complète conçue pour digitaliser les processus administratifs scolaires. Gestion fluide des inscriptions, dossiers étudiants et filières avec tableau de bord analytique.',
    stack: ['React.js', 'Supabase', 'Node.js', 'Vite', 'Vercel'],
    link: 'https://github.com/Yassine-ALAOUI-a11/School-App',
    images: ['/nnn.png'],
    num: '02',
  },
  {
    name: 'Plateforme E-Commerce & Suivi Logistique',
    desc: "Solution d'e-commerce avancée intégrant un système de suivi des produits en temps réel et une gestion complète des livraisons de bout en bout.",
    stack: ['React', 'Node.js', 'Tracking API', 'Tailwind'],
    link: '#',
    images: ['/77.png', '/78.png', '/79.png', '/80.png', '/81.png', '/82.png', '/83.png', '/84.png', '/85.png'],
    num: '03',
  },
];

export default function Projects() {
  const refs = useRef([]);
  const [selected, setSelected] = useState(null);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  useEffect(() => {
    const obs = new IntersectionObserver(
      (entries) => entries.forEach(e => { if (e.isIntersecting) e.target.classList.add('visible'); }),
      { threshold: 0.08 }
    );
    refs.current.forEach(r => r && obs.observe(r));
    return () => obs.disconnect();
  }, []);

  // Close modal on Escape key
  useEffect(() => {
    const handleKey = (e) => { 
      if (e.key === 'Escape') {
        setSelected(null);
        setCurrentImageIndex(0);
      }
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, []);

  const openProject = (p) => {
    setSelected(p);
    setCurrentImageIndex(0);
  };

  const nextImage = (e) => {
    e.stopPropagation();
    if (selected && selected.images) {
      setCurrentImageIndex((prev) => (prev + 1) % selected.images.length);
    }
  };

  const prevImage = (e) => {
    e.stopPropagation();
    if (selected && selected.images) {
      setCurrentImageIndex((prev) => (prev - 1 + selected.images.length) % selected.images.length);
    }
  };

  return (
    <section id="projects">
      <div className="container">
        <p className="section-label reveal" ref={el => refs.current[0] = el}>Mes Réalisations</p>
        <h2 className="section-title reveal" ref={el => refs.current[1] = el} style={{ transitionDelay: '0.1s' }}>
          Projets <em>sélectionnés</em>
        </h2>

        <div className="projects-grid">
          {projects.map((p, i) => (
            <div
              key={p.num}
              className="project-row reveal"
              ref={el => refs.current[2 + i] = el}
              style={{ transitionDelay: `${0.07 * i}s`, cursor: 'pointer' }}
              onClick={() => openProject(p)}
            >
              <div className="project-num">{p.num}</div>
              <div className="project-info">
                <h3 className="project-name">{p.name}</h3>
                <p className="project-desc">{p.desc}</p>
                <div className="project-stack">
                  {p.stack.map(t => (
                    <span key={t} className="project-tag">{t}</span>
                  ))}
                </div>
              </div>
              <span className="project-arrow"><ArrowUpRight size={24} /></span>
            </div>
          ))}
        </div>

        <div style={{ textAlign: 'center', marginTop: 50 }}>
          <a
            href="https://github.com/Yassine-ALAOUI-a11"
            target="_blank" rel="noreferrer"
            className="btn-outline"
            style={{ display: 'inline-flex', alignItems: 'center', gap: '8px' }}
          >
            Voir tous les projets sur GitHub <Code size={18} />
          </a>
        </div>
      </div>

      {/* ═══ MODAL PREVIEW ═══ */}
      {selected && (
        <div className="project-modal-overlay" onClick={() => setSelected(null)}>
          <div className="project-modal" onClick={e => e.stopPropagation()}>
            <button className="project-modal-close" onClick={() => setSelected(null)}><X size={24} /></button>
            <h3 className="project-modal-title">{selected.name}</h3>
            
            <div className="project-modal-gallery">
              <div className="project-modal-image-wrap">
                <img src={selected.images[currentImageIndex]} alt={`Aperçu de ${selected.name}`} />
              </div>
              
              {selected.images.length > 1 && (
                <>
                  <button className="gallery-btn gallery-prev" onClick={prevImage}><ChevronLeft size={20} /></button>
                  <button className="gallery-btn gallery-next" onClick={nextImage}><ChevronRight size={20} /></button>
                  <div className="gallery-dots">
                    {selected.images.map((_, idx) => (
                      <span 
                        key={idx} 
                        className={`gallery-dot ${idx === currentImageIndex ? 'active' : ''}`}
                        onClick={(e) => { e.stopPropagation(); setCurrentImageIndex(idx); }}
                      />
                    ))}
                  </div>
                </>
              )}
            </div>

            <p className="project-modal-desc">{selected.desc}</p>
            <div className="project-stack" style={{ justifyContent: 'center', marginBottom: 20 }}>
              {selected.stack.map(t => (
                <span key={t} className="project-tag">{t}</span>
              ))}
            </div>
            {selected.link !== '#' && (
              <a
                href={selected.link}
                target="_blank"
                rel="noreferrer"
                className="btn-primary"
                style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}
              >
                Voir sur GitHub <ExternalLink size={18} />
              </a>
            )}
          </div>
        </div>
      )}
    </section>
  );
}
